import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl, authFetch } from "../config";
import styles from "./FbReelsTab.module.css";

interface Reel {
  id: number;
  recipe_name: string;
  facebook_account_id?: number | null;
  scheduled_at?: string | null;
}

interface Account {
  id: number;
  name: string;
  facebook_brand_name: string;
}

interface FacebookMetricoolItem {
  id: string;
  kind: "post" | "reel";
  text: string;
  published_at: string;
  url: string;
  thumbnail_url: string;
  views: number;
  reach: number;
  video_views: number;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  engagement: number;
  watch_time_seconds: number;
  average_watch_time_seconds: number;
}

interface FacebookAccountAnalytics {
  account_id: number;
  posts: FacebookMetricoolItem[];
  reels: FacebookMetricoolItem[];
  error?: string;
}

type PageSortMode = "name" | "top" | "missing_today" | "missing_tomorrow";

const CALENDAR_TIME_ZONE = "Africa/Casablanca";

function dateInputValue(date: Date) {
  return date.toISOString().slice(0, 10);
}

function compactMetric(value: number) {
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

function calendarDateKey(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CALENDAR_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: "year" | "month" | "day") => parts.find(item => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function FacebookCalendarTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [reels, setReels] = useState<Reel[]>([]);
  const [analyticsFrom, setAnalyticsFrom] = useState(() => dateInputValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [analyticsTo, setAnalyticsTo] = useState(() => dateInputValue(new Date()));
  const [analyticsByAccount, setAnalyticsByAccount] = useState<Record<number, FacebookAccountAnalytics>>({});
  const [loading, setLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [showAnalyticsPosts, setShowAnalyticsPosts] = useState(false);
  const [pageSort, setPageSort] = useState<PageSortMode>("name");
  const [expandedPostPages, setExpandedPostPages] = useState<Set<number>>(() => new Set());

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setAnalyticsError("");
    try {
      const params = new URLSearchParams({ from: analyticsFrom, to: analyticsTo, timezone: CALENDAR_TIME_ZONE });
      const [reelsResponse, accountsResponse, analyticsResponse] = await Promise.all([
        authFetch(apiUrl("/api/fb-reels")),
        authFetch(apiUrl("/api/facebook-metricool/accounts")),
        authFetch(apiUrl(`/api/facebook-metricool/analytics?${params.toString()}`)),
      ]);

      if (!reelsResponse.ok) throw new Error("Could not load scheduled Reels");
      if (!accountsResponse.ok) throw new Error("Could not load Facebook Pages");
      const reelsData = await reelsResponse.json() as { reels?: Reel[] };
      const accountsData = await accountsResponse.json() as { accounts?: Account[] };
      setReels(reelsData.reels || []);
      setAccounts(accountsData.accounts || []);

      const analyticsData = await analyticsResponse.json() as { accounts?: FacebookAccountAnalytics[]; error?: string };
      if (!analyticsResponse.ok) throw new Error(analyticsData.error || "Metricool analytics failed");
      setAnalyticsByAccount(Object.fromEntries((analyticsData.accounts || []).map(item => [item.account_id, item])));
    } catch (error) {
      setAnalyticsError(error instanceof Error ? error.message : "Facebook calendar could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [analyticsFrom, analyticsTo]);

  useEffect(() => {
    void loadCalendar();
    // Date changes are applied only when the user presses Refresh calendar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduledByAccount = useMemo(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const todayKey = calendarDateKey(now);
    const tomorrowKey = calendarDateKey(tomorrow);

    const rows = accounts.map(account => {
      const posts = reels
        .filter(reel => reel.facebook_account_id === account.id && reel.scheduled_at)
        .sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)));
      const analytics = analyticsByAccount[account.id];
      const metricoolItems = [...(analytics?.posts || []), ...(analytics?.reels || [])]
        .sort((a, b) => String(b.published_at).localeCompare(String(a.published_at)));
      const totals = metricoolItems.reduce((sum, item) => ({
        views: sum.views + item.views,
        reach: sum.reach + item.reach,
        reactions: sum.reactions + item.reactions,
        actions: sum.actions + item.comments + item.shares + item.clicks,
      }), { views: 0, reach: 0, reactions: 0, actions: 0 });
      return {
        account,
        posts,
        analytics,
        metricoolItems,
        totals,
        todayCount: posts.filter(post => calendarDateKey(post.scheduled_at!) === todayKey).length,
        tomorrowCount: posts.filter(post => calendarDateKey(post.scheduled_at!) === tomorrowKey).length,
      };
    });

    return rows.sort((a, b) => {
      if (pageSort === "top") return b.totals.views - a.totals.views || b.totals.reach - a.totals.reach || a.account.name.localeCompare(b.account.name);
      if (pageSort === "missing_today") return a.todayCount - b.todayCount || b.totals.views - a.totals.views || a.account.name.localeCompare(b.account.name);
      if (pageSort === "missing_tomorrow") return a.tomorrowCount - b.tomorrowCount || b.totals.views - a.totals.views || a.account.name.localeCompare(b.account.name);
      return a.account.name.localeCompare(b.account.name);
    });
  }, [accounts, reels, analyticsByAccount, pageSort]);

  return <div className={styles.page}>
    <section className={styles.scheduleOverview}>
      <div className={styles.scheduleOverviewHeader}>
        <div><span>METRICOOL CALENDAR</span><h2>Facebook Page posts</h2><small>Scheduled Reels and publishing performance across every connected Page.</small></div>
        <div className={styles.analyticsControls}>
          <label>Sort Pages<select value={pageSort} onChange={event => setPageSort(event.target.value as PageSortMode)}><option value="name">Page name</option><option value="top">Top pages</option><option value="missing_today">No post today first</option><option value="missing_tomorrow">No post tomorrow first</option></select></label>
          <label>From<input type="date" value={analyticsFrom} onChange={event => setAnalyticsFrom(event.target.value)} /></label>
          <label>To<input type="date" value={analyticsTo} onChange={event => setAnalyticsTo(event.target.value)} /></label>
          <button onClick={() => void loadCalendar()} disabled={loading}>{loading ? "Loading…" : "Refresh calendar"}</button>
          <button type="button" className={showAnalyticsPosts ? styles.analyticsPostsToggleActive : ""} onClick={() => setShowAnalyticsPosts(value => !value)}>{showAnalyticsPosts ? "Hide analytics posts" : "Show analytics posts"}</button>
        </div>
      </div>
      {analyticsError && <div className={styles.analyticsGlobalError}>{analyticsError}</div>}
      {!loading && !accounts.length && !analyticsError && <div className={styles.emptyPageState}>No Metricool Facebook Pages are configured yet.</div>}
      <div className={styles.pageScheduleGrid}>{scheduledByAccount.map(({ account, posts, analytics, metricoolItems, totals, todayCount, tomorrowCount }) => {
        const postsExpanded = expandedPostPages.has(account.id);
        const visiblePosts = postsExpanded ? posts : posts.slice(-3);
        const hiddenPostCount = Math.max(0, posts.length - visiblePosts.length);
        return <article className={`${styles.pageScheduleCard} ${totals.views >= 50_000 ? styles.pageScheduleCardPopular : ""}`} key={account.id}>
          <div className={styles.pageScheduleTitle}><div><strong>{account.name}</strong><span>{account.facebook_brand_name}</span><small className={styles.pageDayCounts}><i className={!todayCount ? styles.missingDay : ""}>Today {todayCount}</i><i className={!tomorrowCount ? styles.missingDay : ""}>Tomorrow {tomorrowCount}</i></small></div><b>{posts.length} scheduled</b></div>
          <div className={styles.pageCardSectionTitle}><span>Scheduled from DishSnap</span><b>{posts.length}</b></div>
          <div className={styles.pagePostList}>{visiblePosts.map(post => {
            const date = new Date(post.scheduled_at!);
            return <div className={styles.pagePostRow} key={post.id}><div><strong>{post.recipe_name}</strong><span>{date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span></div><time dateTime={post.scheduled_at!}>{date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</time><b>Done</b></div>;
          })}{!posts.length && <div className={styles.emptyPageState}>No DishSnap posts scheduled for this Page.</div>}
            {posts.length > 3 && <button type="button" className={styles.pagePostsToggle} onClick={() => setExpandedPostPages(current => {
              const next = new Set(current);
              if (next.has(account.id)) next.delete(account.id);
              else next.add(account.id);
              return next;
            })}>{postsExpanded ? "Show latest 3" : `Show ${hiddenPostCount} older post${hiddenPostCount === 1 ? "" : "s"}`}</button>}
          </div>
          <div className={styles.pageCardSectionTitle}><span>Metricool analytics</span><b>{metricoolItems.length} published</b></div>
          {loading && !analytics && <div className={styles.emptyPageState}>Loading Page analytics…</div>}
          {analytics?.error && <div className={styles.pageAnalyticsError}>{analytics.error}</div>}
          {analytics && <>
            <div className={styles.analyticsSummary}>
              <div><strong>{compactMetric(totals.views)}</strong><span>Views</span></div>
              <div><strong>{compactMetric(totals.reach)}</strong><span>Reach</span></div>
              <div><strong>{compactMetric(totals.reactions)}</strong><span>Reactions</span></div>
              <div><strong>{compactMetric(totals.actions)}</strong><span>Actions</span></div>
            </div>
            {showAnalyticsPosts && <div className={styles.metricoolPostList}>{metricoolItems.slice(0, 20).map((item, index) => {
              const published = item.published_at ? new Date(item.published_at) : null;
              const performanceClass = item.views >= 10_000 ? styles.metricoolPostViral : item.views >= 1_500 ? styles.metricoolPostHigh : "";
              return <div className={`${styles.metricoolPostRow} ${performanceClass}`} key={`${item.kind}-${item.id || index}`}>
                {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" /> : <div className={styles.metricoolMediaFallback}>{item.kind === "reel" ? "▶" : "f"}</div>}
                <div className={styles.metricoolPostCopy}>
                  <div><b>{item.kind}</b><span>{published && !Number.isNaN(published.getTime()) ? published.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Published"}</span></div>
                  {item.url ? <a href={item.url} target="_blank" rel="noreferrer">{item.text}</a> : <strong>{item.text}</strong>}
                  <small><span>◉ {compactMetric(item.views)} views</span><span>◎ {compactMetric(item.reach)} reach</span><span>♥ {compactMetric(item.reactions)}</span><span>↗ {compactMetric(item.shares + item.comments + item.clicks)}</span></small>
                </div>
              </div>;
            })}{!metricoolItems.length && !analytics?.error && <div className={styles.emptyPageState}>No Facebook posts or Reels in this date range.</div>}</div>}
          </>}
        </article>;
      })}</div>
    </section>
  </div>;
}
